(() => {
  const customerPrivacyFeature = [{ name: 'consent-tracking-api', version: '0.1' }];
  const retryDelay = 200;
  const maxRetries = 50;
  const preferencesAvailableClass = 'termly-preferences-available';

  let termlyBound = false;
  let termlyBindRetryCount = 0;
  let termlyBindRetryTimer = null;
  let featureRequestPending = false;
  let retryCount = 0;
  let retryTimer = null;
  let syncInFlight = false;
  let pendingConsent = null;
  let lastAppliedSignature = '';

  const booleanValue = (value) => (typeof value === 'boolean' ? value : undefined);

  const consentFromCategories = (categories) => {
    if (!Array.isArray(categories)) return null;

    const allowed = new Set(categories);

    return {
      analytics: allowed.has('analytics'),
      performance: allowed.has('performance'),
      advertising: allowed.has('advertising')
    };
  };

  const readTermlyConsent = (eventData) => {
    const eventState = eventData?.consentState;

    if (eventState && typeof eventState === 'object') return eventState;

    const categoryState = consentFromCategories(eventData?.categories);
    if (categoryState) return categoryState;

    if (typeof window.Termly?.getConsentState !== 'function') return null;

    try {
      return window.Termly.getConsentState();
    } catch (error) {
      return null;
    }
  };

  const mapConsentForShopify = (termlyConsent) => {
    if (!termlyConsent || typeof termlyConsent !== 'object') return null;

    const analytics = booleanValue(termlyConsent.analytics);
    const preferences = booleanValue(termlyConsent.performance);
    const advertising = booleanValue(termlyConsent.advertising);
    const consent = {};

    if (analytics !== undefined) consent.analytics = analytics;
    if (preferences !== undefined) consent.preferences = preferences;

    if (advertising !== undefined) {
      consent.marketing = advertising;
      consent.sale_of_data = advertising;
    }

    return Object.keys(consent).length > 0 ? consent : null;
  };

  const scheduleRetry = () => {
    if (retryTimer || retryCount >= maxRetries) return;

    retryCount += 1;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      flushConsent();
    }, retryDelay);
  };

  const requestCustomerPrivacyFeature = () => {
    if (window.Shopify?.customerPrivacy?.setTrackingConsent) return true;

    if (!featureRequestPending && typeof window.Shopify?.loadFeatures === 'function') {
      featureRequestPending = true;
      window.Shopify.loadFeatures(customerPrivacyFeature, (error) => {
        featureRequestPending = false;

        if (error) {
          scheduleRetry();
          return;
        }

        flushConsent();
      });
    }

    scheduleRetry();
    return false;
  };

  function flushConsent() {
    if (!pendingConsent || syncInFlight) return;
    if (!requestCustomerPrivacyFeature()) return;

    const consent = pendingConsent;
    const signature = JSON.stringify(consent);

    if (signature === lastAppliedSignature) return;

    syncInFlight = true;
    let completed = false;

    const complete = (result) => {
      if (completed) return;

      completed = true;
      syncInFlight = false;

      if (!result?.error) {
        lastAppliedSignature = signature;
        retryCount = 0;
        document.dispatchEvent(new CustomEvent('wob:termly-consent-synced', {
          detail: { provider: 'termly' }
        }));
      } else {
        scheduleRetry();
      }

      if (pendingConsent && JSON.stringify(pendingConsent) !== signature) {
        flushConsent();
      }
    };

    try {
      window.Shopify.customerPrivacy.setTrackingConsent(consent, complete);
      window.setTimeout(() => complete({ error: new Error('Shopify consent synchronization timed out') }), 2000);
    } catch (error) {
      complete({ error });
    }
  }

  const queueConsent = (eventData) => {
    const consent = mapConsentForShopify(readTermlyConsent(eventData));

    if (!consent) return;

    pendingConsent = consent;
    flushConsent();
  };

  const scheduleTermlyBind = () => {
    if (termlyBound || termlyBindRetryTimer || termlyBindRetryCount >= maxRetries) return;

    termlyBindRetryCount += 1;
    termlyBindRetryTimer = window.setTimeout(() => {
      termlyBindRetryTimer = null;
      bindTermly();
    }, retryDelay);
  };

  const bindTermly = () => {
    if (termlyBound) return;

    if (typeof window.Termly?.on !== 'function') {
      scheduleTermlyBind();
      return;
    }

    termlyBound = true;
    document.documentElement.classList.add(preferencesAvailableClass);
    window.Termly.on('consent', queueConsent);
    queueConsent();
  };

  document.addEventListener('shopifyCustomerPrivacyApiLoaded', flushConsent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTermly, { once: true });
  } else {
    bindTermly();
  }
})();
